// emailService.js - EmailJS Order Notification Service

import emailjs from '@emailjs/browser';

// EmailJS Configuration
const EMAILJS_CONFIG = {
  SERVICE_ID: 'service_qxmnspc',
  TEMPLATE_ID: 'template_bcmp3rd',
  PUBLIC_KEY: 'ExoaeJcq_Nx1pEZ6N', // Replace with your actual public key
  ADMIN_EMAIL: 'anmolfurniturebazaar@gmail.com' // Replace with your email
};

// Initialize EmailJS
emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);

/**
 * Send order notification email to admin
 * @param {Object} orderData - The order data from Firebase
 * @param {Object} userData - User data (email, etc.)
 */
export const sendOrderNotificationEmail = async (orderData, userData) => {
  try {
    // Calculate subtotal and tax
    const subtotal = orderData.items.reduce((total, item) => total + (item.price * item.quantity), 0);
    const tax = Math.round(subtotal * 0.18);
    
    // Format order date
    const orderDate = new Date(orderData.createdAt).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Prepare email template parameters
    const templateParams = {
      // Order Information
      order_id: orderData.id,
      order_date: orderDate,
      order_status: orderData.status || 'pending',
      
      // Customer Information
      customer_name: orderData.shippingAddress.name,
      customer_email: userData.email || 'N/A',
      customer_phone: orderData.shippingAddress.phone,
      
      // Payment Information
      payment_method: orderData.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Payment (Razorpay)',
      payment_status: orderData.paymentStatus === 'completed' ? 'Paid' : 'Pending',
      payment_id: orderData.paymentId || 'N/A',
      
      // Shipping Address (formatted as single string)
      shipping_address: `
        ${orderData.shippingAddress.name}
        ${orderData.shippingAddress.address}
        ${orderData.shippingAddress.city}, ${orderData.shippingAddress.state} - ${orderData.shippingAddress.pincode}
        Phone: ${orderData.shippingAddress.phone}
      `.trim(),
      
      // Items (formatted as HTML list)
      items_list: orderData.items.map((item, index) => `
        ${index + 1}. ${item.name}
        - Quantity: ${item.quantity}
        - Price: ₹${item.price.toLocaleString()}
        - Total: ₹${(item.price * item.quantity).toLocaleString()}
      `).join('\n'),
      
      // Items count
      items_count: orderData.items.length,
      
      // Individual item details (for template loops if needed)
      item_1_name: orderData.items[0]?.name || '',
      item_1_quantity: orderData.items[0]?.quantity || 0,
      item_1_price: orderData.items[0]?.price || 0,
      item_1_total: orderData.items[0] ? (orderData.items[0].price * orderData.items[0].quantity) : 0,
      
      // Pricing
      subtotal: subtotal.toLocaleString(),
      tax_amount: tax.toLocaleString(),
      total_amount: orderData.total.toLocaleString(),
      
      // Admin email (recipient)
      admin_email: EMAILJS_CONFIG.ADMIN_EMAIL,
      
      // Additional metadata
      user_id: orderData.userId || 'N/A',
      created_at: orderData.createdAt,
      updated_at: orderData.updatedAt || orderData.createdAt
    };

    // Send email using EmailJS
    const response = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID,
      templateParams
    );

    console.log('✅ Order notification email sent successfully:', response);
    return { success: true, response };

  } catch (error) {
    console.error('❌ Error sending order notification email:', error);
    return { success: false, error };
  }
};

/**
 * Enhanced version with retry mechanism
 */
export const sendOrderNotificationWithRetry = async (orderData, userData, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await sendOrderNotificationEmail(orderData, userData);
      if (result.success) {
        return result;
      }
      lastError = result.error;
    } catch (error) {
      lastError = error;
      console.warn(`Email send attempt ${attempt} failed:`, error);
    }
    
    // Wait before retrying (exponential backoff)
    if (attempt < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }
  
  console.error(`Failed to send email after ${maxRetries} attempts:`, lastError);
  return { success: false, error: lastError };
};

// Integration function to call from your checkout component
export const handleOrderNotification = async (orderData, userData) => {
  try {
    // Send notification email to admin
    const emailResult = await sendOrderNotificationWithRetry(orderData, userData);

    if (emailResult.success) {
      console.log('Order notification sent to admin');
    } else {
      console.error('Failed to send order notification:', emailResult.error);
      // You might want to queue this for retry later or use an alternative notification method
    }

    return emailResult;
  } catch (error) {
    console.error('Error in handleOrderNotification:', error);
    return { success: false, error };
  }
};

/**
 * Send enquiry notification email to admin
 * @param {Object} enquiryData - The enquiry form data
 */
export const sendEnquiryNotificationEmail = async (enquiryData) => {
  try {
    // Format enquiry date
    const enquiryDate = new Date().toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Prepare email template parameters for enquiry
    const templateParams = {
      // Enquiry Information
      enquiry_date: enquiryDate,
      subject: 'New Enquiry',

      // Customer Information
      customer_name: enquiryData.name,
      customer_email: enquiryData.email,
      customer_phone: enquiryData.phone,
      customer_address: enquiryData.address,

      // Enquiry Details
      enquiry_message: enquiryData.enquiry,

      // Admin email (recipient)
      admin_email: EMAILJS_CONFIG.ADMIN_EMAIL,

      // Additional metadata
      enquiry_type: 'Website Enquiry Form'
    };

    // Send email using EmailJS
    const response = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID,
      templateParams
    );

    console.log('✅ Enquiry notification email sent successfully:', response);
    return { success: true, response };

  } catch (error) {
    console.error('❌ Error sending enquiry notification email:', error);
    return { success: false, error };
  }
};

/**
 * Enhanced enquiry notification with retry mechanism
 */
export const sendEnquiryNotificationWithRetry = async (enquiryData, maxRetries = 3) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await sendEnquiryNotificationEmail(enquiryData);
      if (result.success) {
        return result;
      }
      lastError = result.error;
    } catch (error) {
      lastError = error;
      console.warn(`Enquiry email send attempt ${attempt} failed:`, error);
    }

    // Wait before retrying (exponential backoff)
    if (attempt < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }

  console.error(`Failed to send enquiry email after ${maxRetries} attempts:`, lastError);
  return { success: false, error: lastError };
};

// Integration function to call from your enquiry modal
export const handleEnquiryNotification = async (enquiryData) => {
  try {
    // Send notification email to admin
    const emailResult = await sendEnquiryNotificationWithRetry(enquiryData);

    if (emailResult.success) {
      console.log('Enquiry notification sent to admin');
    } else {
      console.error('Failed to send enquiry notification:', emailResult.error);
      // You might want to queue this for retry later or use an alternative notification method
    }

    return emailResult;
  } catch (error) {
    console.error('Error in handleEnquiryNotification:', error);
    return { success: false, error };
  }
};

